import React from "react";
import { motion } from "framer-motion";
import { Sparkles, ArrowRight } from "lucide-react";
import { 
  SiReact, SiNextdotjs, SiWordpress, SiShopify, 
  SiApple, SiAndroid, SiPhp, SiNodedotjs 
} from "react-icons/si";

export default function Hometechused4() {
  const technologies = [
    { 
      name: "React.js / Next.js", 
      desc: "High Performance Frontend Architecture",
      subTitle: "Modern Web Apps & SSR",
      logos: [
        { name: "React", icon: React.createElement(SiReact as any, { className: "text-[#61DAFB]", size: 24 }) },
        { name: "Next.js", icon: React.createElement(SiNextdotjs as any, { className: "text-slate-900 dark:text-white", size: 24 }) }
      ],
      glowColor: "from-cyan-500/20 to-blue-500/20"
    },
    { 
      name: "WordPress & Shopify", 
      desc: "Scalable E-Commerce & CMS Platforms",
      subTitle: "Turnkey Online Storefronts",
      logos: [
        { name: "WordPress", icon: React.createElement(SiWordpress as any, { className: "text-[#21759B]", size: 24 }) },
        { name: "Shopify", icon: React.createElement(SiShopify as any, { className: "text-[#96BF48]", size: 24 }) }
      ],
      glowColor: "from-emerald-500/20 to-teal-500/20"
    },
    { 
      name: "iOS & Android", 
      desc: "Native & React Native Mobile Apps",
      subTitle: "Dual App Store Publishing",
      logos: [
        { name: "Apple iOS", icon: React.createElement(SiApple as any, { className: "text-slate-900 dark:text-white", size: 24 }) },
        { name: "Android", icon: React.createElement(SiAndroid as any, { className: "text-[#3DDC84]", size: 24 }) },
        { name: "React Native", icon: React.createElement(SiReact as any, { className: "text-[#61DAFB]", size: 22 }) }
      ],
      glowColor: "from-blue-500/20 to-indigo-500/20"
    },
    { 
      name: "PHP & Node.js", 
      desc: "Secure Enterprise Backend & APIs",
      subTitle: "High-Concurreny Cloud Microservices",
      logos: [
        { name: "PHP", icon: React.createElement(SiPhp as any, { className: "text-[#777BB4]", size: 26 }) },
        { name: "Node.js", icon: React.createElement(SiNodedotjs as any, { className: "text-[#5FA04E]", size: 24 }) }
      ],
      glowColor: "from-purple-500/20 to-violet-500/20"
    },
  ];

  return (
    <div className="w-full py-16 px-4 md:py-24 lg:py-28 bg-[#F8FAFC] dark:bg-[#0B0F19] text-[#0F172A] dark:text-[#F8FAFC] font-sans flex items-center justify-center transition-colors duration-300 relative overflow-hidden">
      
      {/* BACKGROUND AMBIENT GLOW LIGHTS */}
      <div className="absolute top-1/4 left-1/2 -translate-x-1/2 w-[600px] h-[300px] bg-gradient-to-r from-blue-500/10 via-cyan-500/10 to-indigo-500/10 blur-3xl pointer-events-none rounded-full" />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full flex flex-col justify-center items-center relative z-10 space-y-12">
        
        {/* Section Header */}
        <header className="max-w-3xl mx-auto text-center space-y-3">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-blue-50 dark:bg-blue-500/15 border border-blue-200 dark:border-blue-500/30 text-blue-700 dark:text-cyan-400">
            <Sparkles size={14} />
            <span className="text-xs font-bold uppercase tracking-wider">
              Cutting-Edge Tech Stack
            </span>
          </div>

          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-slate-900 dark:text-white tracking-tight">
            Technologies <span className="text-blue-600 dark:text-cyan-400">We Master</span>
          </h2>
          <p className="text-sm sm:text-base text-slate-600 dark:text-slate-400 max-w-xl mx-auto font-medium leading-relaxed">
            From modern JavaScript frameworks to enterprise backend architectures, we build bulletproof digital solutions.
          </p>
        </header>

        {/* Responsive Grid with Animated Light Visual Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 lg:gap-8 items-stretch w-full">
          {technologies.map((tech, idx) => (
            <motion.div 
              key={idx} 
              whileHover={{ y: -6, scale: 1.02 }}
              transition={{ duration: 0.2 }}
              className="group relative overflow-hidden backdrop-blur-xl bg-white/80 dark:bg-slate-900/80 border border-slate-200/90 dark:border-slate-800/90 rounded-3xl p-7 space-y-6 shadow-xl hover:shadow-2xl hover:border-blue-500/50 dark:hover:border-cyan-500/50 transition-all flex flex-col justify-between cursor-pointer"
            >
              {/* BLURRY BACKDROP GRADIENT GLOW */}
              <div className={`absolute -top-12 -right-12 w-32 h-32 bg-gradient-to-br ${tech.glowColor} rounded-full blur-2xl group-hover:scale-150 transition-transform pointer-events-none`} />

              <div className="space-y-4 relative z-10">
                {/* ANIMATED FLOATING LOGOS BADGE CONTAINER */}
                <div className="flex items-center gap-2.5">
                  {tech.logos.map((logo, lIdx) => (
                    <motion.div 
                      key={lIdx}
                      animate={{ y: [0, -4, 0] }}
                      transition={{ repeat: Infinity, duration: 3, ease: "easeInOut", delay: (idx * 0.2) + (lIdx * 0.15) }}
                      className="p-3 rounded-2xl bg-slate-100/90 dark:bg-slate-800/90 border border-slate-200 dark:border-slate-700/80 shadow-md group-hover:scale-110 transition-transform flex items-center justify-center"
                      title={logo.name}
                    >
                      {logo.icon}
                    </motion.div>
                  ))}
                </div>

                <div className="space-y-1.5">
                  <h3 className="text-xl font-extrabold text-slate-900 dark:text-white tracking-tight group-hover:text-blue-600 dark:group-hover:text-cyan-400 transition-colors">
                    {tech.name}
                  </h3>
                  <span className="text-[11px] font-bold uppercase tracking-wider text-blue-600 dark:text-cyan-400 block">
                    ⚡ {tech.subTitle}
                  </span>
                  <p className="text-xs sm:text-sm text-slate-600 dark:text-slate-400 font-medium leading-relaxed pt-1">
                    {tech.desc}
                  </p>
                </div>
              </div>

              <div className="pt-4 border-t border-slate-100 dark:border-slate-800/80 relative z-10 flex items-center justify-between">
                <span className="text-[11px] font-bold text-slate-500 dark:text-slate-400">Enterprise Verified</span>
                <ArrowRight size={14} className="text-blue-600 dark:text-cyan-400 group-hover:translate-x-1 transition-transform" />
              </div>
            </motion.div>
          ))}
        </div>

      </div>
    </div>
  );
}
